import translations from '@/data/translations.json'
import { getDatasetModule } from '../config/datasets'

/**
 * Translation composable for ChartsEmbedder.
 * Resolves labels from translations.json with multi-level fallback.
 * When a dataset UI profile exists, filter labels from the profile take priority.
 *
 * @param {import('@vue/composition-api').Ref<string>} langRef - reactive language code
 * @param {import('@vue/composition-api').Ref<string>} [datasetRef] - reactive dataset key (optional)
 * @returns {{ t: Function, translateFilter: Function }}
 */
export function useChartTranslations(langRef, datasetRef) {
  const getLang = () => (typeof langRef === 'object' ? langRef.value : langRef) || 'en'

  const t = (key) => {
    const lang = getLang()
    const langBlock = translations[lang]

    if (langBlock) {
      if (typeof langBlock[key] === 'string') return langBlock[key]
      if (langBlock.columns && typeof langBlock.columns[key] === 'string') return langBlock.columns[key]
      if (langBlock.views && typeof langBlock.views[key] === 'string') return langBlock.views[key]
    }

    const enBlock = translations['en']
    if (enBlock) {
      if (typeof enBlock[key] === 'string') return enBlock[key]
      if (enBlock.columns && typeof enBlock.columns[key] === 'string') return enBlock.columns[key]
      if (enBlock.views && typeof enBlock.views[key] === 'string') return enBlock.views[key]
    }

    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  const translateFilter = (filterKey, pfLabel = null) => {
    const lang = getLang()

    // Priority 1: dataset UI profile label
    if (datasetRef) {
      const dataset = typeof datasetRef === 'object' ? datasetRef.value : datasetRef
      const module = getDatasetModule(dataset)
      if (module) {
        let entry = null

        if (filterKey === 'question' && pfLabel) {
          entry = module.filters.find(f => f.key === filterKey && f.filterType === 'process-form' && (f.forField || f.label) === pfLabel)
        }

        if (!entry) {
          entry = module.filters.find(f => f.key === filterKey)
        }

        if (entry && entry.label) {
          if (typeof entry.label === 'string') return entry.label
          return entry.label[lang] || entry.label['en'] || entry.label['es'] || t(filterKey)
        }
      }
    }

    // Priority 2: translations.json columns block
    const langBlock = translations[lang]
    if (langBlock && langBlock.columns && typeof langBlock.columns[filterKey] === 'string') {
      return langBlock.columns[filterKey]
    }

    // Priority 3: general t() fallback
    return t(filterKey)
  }

  return { t, translateFilter }
}
